/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access, typescript/no-unsafe-return -- schema library uses any types throughout */
import { BaseSchema } from '../core/base-schema'
import type * as errors from '../core/errors'
import * as types from '../core/types'
import type { DefaultSchema } from './wrappers'
import { _default as makeDefault } from './wrappers'
import type { OptionalSchema, NullableSchema } from '../core/base-schema'

// Enum schema definition
export interface EnumSchemaDef<T extends readonly [string, ...string[]]>
  extends types.SchemaTypeDef {
  type: 'enum'
  values: T
}

export interface EnumSchemaInternals<
  T extends readonly [string, ...string[]],
> extends types.SchemaInternals<T[number], T[number]> {
  def: EnumSchemaDef<T>
}

export interface EnumSchema<
  T extends readonly [string, ...string[]],
> extends types.Schema<T[number], T[number], EnumSchemaInternals<T>> {
  readonly options: T
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  default(defaultValue: T[number] | (() => T[number])): DefaultSchema<this>
  meta(metadata: Record<string, any>): this
  parse(data: unknown, params?: types.ParseContext): T[number]
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: T[number] }
    | { success: false; error: errors.SchemaError }
}

export const EnumSchema = types.$constructor<EnumSchema<any>>(
  'EnumSchema',
  (inst, def: EnumSchemaDef<any>) => {
    BaseSchema.init(inst, def)

    inst._zod.parse = (payload, _ctx) => {
      if (def.values.includes(payload.value)) return payload

      payload.issues.push({
        code: 'invalid_enum_value',
        options: def.values,
        input: payload.value,
        inst,
      })
      return payload
    }

    Object.defineProperty(inst, 'options', {
      value: def.values,
      enumerable: true,
    })

    inst.default = (defaultValue: any) => {
      return makeDefault(inst, defaultValue)
    }
  },
)

export function _enum<const T extends readonly [string, ...string[]]>(
  values: T,
): EnumSchema<T> {
  return new EnumSchema({
    type: 'enum',
    values,
  })
}
